import * as React from 'react';
import { Eye, EyeOff } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface PasswordFieldProps
  extends React.ComponentPropsWithoutRef<'input'> {
  /** Accessible label for the show/hide toggle button. */
  toggleLabel?: string;
}

/**
 * Password input with an inline show/hide toggle. Forwards its ref and all
 * native input props so it drops into react-hook-form via `{...register(...)}`.
 */
export const PasswordField = React.forwardRef<
  HTMLInputElement,
  PasswordFieldProps
>(
  (
    {
      className,
      toggleLabel = 'Toggle password visibility',
      autoComplete = 'current-password',
      ...props
    },
    ref,
  ) => {
    const [show, setShow] = React.useState(false);

    return (
      <div className="relative">
        <Input
          ref={ref}
          type={show ? 'text' : 'password'}
          autoComplete={autoComplete}
          className={cn('pr-11', className)}
          {...props}
        />
        <button
          type="button"
          onClick={() => setShow((prev) => !prev)}
          aria-label={toggleLabel}
          aria-pressed={show}
          className="absolute right-1 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-md text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none"
        >
          {show ? (
            <EyeOff aria-hidden="true" className="h-4 w-4" />
          ) : (
            <Eye aria-hidden="true" className="h-4 w-4" />
          )}
        </button>
      </div>
    );
  },
);

PasswordField.displayName = 'PasswordField';
